//! moment-timezone.js
//! version : 0.2.2
//! author : Tim Wood
//! license : MIT
//! github.com/moment/moment-timezone

(function (root, factory) {
    "use strict";

    /*global define*/
    if (typeof define === 'function' && define.amd) {
        define(['moment'], factory);                 // AMD
    } else if (typeof exports === 'object') {
        module.exports = factory(require('moment')); // Node
    } else {
        factory(root.moment);                        // Browser
    }
}(this, function (moment) {
    "use strict";

    // Do not load moment-timezone a second time.
    if (moment.tz !== undefined) { return moment; }

    var VERSION = "0.2.2",
        zones = {},
        links = {},

        momentVersion = moment.version.split('.'),
        major = +momentVersion[0],
        minor = +momentVersion[1];

    // Moment.js version check
    if (major < 2 || (major === 2 && minor < 6)) {
        logError('Moment Timezone requires Moment.js >= 2.6.0. You are using Moment.js ' + moment.version + '. See momentjs.com');
    }

    /************************************
        Unpacking
    ************************************/

    function charCodeToInt(charCode) {
        if (charCode > 96) {
            return charCode - 87;
        } else if (charCode > 64) {
            return charCode - 29;
        }
        return charCode - 48;
    }

    function unpackBase60(string) {
        var i = 0,
            parts = string.split('.'),
            whole = parts[0],
            fractional = parts[1] || '',
            multiplier = 1,
            num,
            out = 0,
            sign = 1;

        // handle negative numbers
        if (string.charCodeAt(0) === 45) {
            i = 1;
            sign = -1;
        }

        // handle digits before the decimal
        for (i; i < whole.length; i++) {
            num = charCodeToInt(whole.charCodeAt(i));
            out = 60 * out + num;
        }

        // handle digits after the decimal
        for (i = 0; i < fractional.length; i++) {
            multiplier = multiplier / 60;
            num = charCodeToInt(fractional.charCodeAt(i));
            out += num * multiplier;
        }

        return out * sign;
    }

    function arrayToInt (array) {
        for (var i = 0; i < array.length; i++) {
            array[i] = unpackBase60(array[i]);
        }
    }

    function intToUntil (array, length) {
        for (var i = 0; i < length; i++) {
            array[i] = Math.round((array[i - 1] || 0) + (array[i] * 60000)); // minutes to milliseconds
        }

        array[length - 1] = Infinity;
    }

    function mapIndices (source, indices) {
        var out = [], i;

        for (i = 0; i < indices.length; i++) {
            out[i] = source[indices[i]];
        }

        return out;
    }

    function unpack (string) {
        var data = string.split('|'),
            offsets = data[2].split(' '),
            indices = data[3].split(''),
            untils  = data[4].split(' ');

        arrayToInt(offsets);
        arrayToInt(indices);
        arrayToInt(untils);

        intToUntil(untils, indices.length);

        return {
            name    : data[0],
            abbrs   : mapIndices(data[1].split(' '), indices),
            offsets : mapIndices(offsets, indices),
            untils  : untils
        };
    }

    /************************************
        Zone object
    ************************************/

    function Zone (packedString) {
        if (packedString) {
            this._set(unpack(packedString));
        }
    }

    Zone.prototype = {
        _set : function (unpacked) {
            this.name    = unpacked.name;
            this.abbrs   = unpacked.abbrs;
            this.untils  = unpacked.untils;
            this.offsets = unpacked.offsets;
        },

        _index : function (timestamp) {
            var target = +timestamp,
                untils = this.untils,
                i;

            for (i = 0; i < untils.length; i++) {
                if (target < untils[i]) {
                    return i;
                }
            }
        },

        parse : function (timestamp) {
            var target  = +timestamp,
                offsets = this.offsets,
                untils  = this.untils,
                max     = untils.length - 1,
                offset, offsetNext, offsetPrev, i;

            for (i = 0; i < max; i++) {
                offset     = offsets[i];
                offsetNext = offsets[i + 1];
                offsetPrev = offsets[i ? i - 1 : i];

                if (offset < offsetNext && tz.moveAmbiguousForward) {
                    offset = offsetNext;
                } else if (offset > offsetPrev && tz.moveInvalidForward) {
                    offset = offsetPrev;
                }

                if (target < untils[i] - (offset * 60000)) {
                    return offsets[i];
                }
            }

            return offsets[max];
        },

        abbr : function (mom) {
            return this.abbrs[this._index(mom)];
        },

        offset : function (mom) {
            return this.offsets[this._index(mom)];
        }
    };

    /************************************
        Global Methods
    ************************************/

    function normalizeName (name) {
        return (name || '').toLowerCase().replace(/\//g, '_');
    }

    function addZone (packed) {
        var i, zone, zoneName;

        if (typeof packed === "string") {
            packed = [packed];
        }

        for (i = 0; i < packed.length; i++) {
            zone = new Zone(packed[i]);
            zoneName = normalizeName(zone.name);
            zones[zoneName] = zone;
            upgradeLinksToZones(zoneName);
        }
    }

    function getZone (name) {
        return zones[normalizeName(name)] || null;
    }

    function getNames () {
        var i, out = [];

        for (i in zones) {
            if (zones.hasOwnProperty(i) && zones[i]) {
                out.push(zones[i].name);
            }
        }

        return out.sort();
    }

    function addLink (aliases) {
        var i, alias;

        if (typeof aliases === "string") {
            aliases = [aliases];
        }

        for (i = 0; i < aliases.length; i++) {
            alias = aliases[i].split('|');
            pushLink(alias[0], alias[1]);
            pushLink(alias[1], alias[0]);
        }
    }

    function upgradeLinksToZones (zoneName) {
        if (!links[zoneName]) {
            return;
        }

        var i,
            zone = zones[zoneName],
            linkNames = links[zoneName];

        for (i = 0; i < linkNames.length; i++) {
            copyZoneWithName(zone, linkNames[i]);
        }

        links[zoneName] = null;
    }

    function copyZoneWithName (zone, name) {
        var linkZone = zones[normalizeName(name)] = new Zone();
        linkZone._set(zone);
        linkZone.name = name;
    }

    function pushLink (zoneName, linkName) {
        zoneName = normalizeName(zoneName);

        if (zones[zoneName]) {
            copyZoneWithName(zones[zoneName], linkName);
        } else {
            links[zoneName] = links[zoneName] || [];
            links[zoneName].push(linkName);
        }
    }

    function loadData (data) {
        addZone(data.zones);
        addLink(data.links);
        tz.dataVersion = data.version;
    }

    function zoneExists (name) {
        if (!zoneExists.didShowError) {
            zoneExists.didShowError = true;
                logError("moment.tz.zoneExists('" + name + "') has been deprecated in favor of !moment.tz.zone('" + name + "')");
        }
        return !!getZone(name);
    }

    function needsOffset (m) {
        return !!(m._a && (m._tzm === undefined));
    }

    function logError (message) {
        if (typeof console !== 'undefined' && typeof console.error === 'function') {
            console.error(message);
        }
    }

    /************************************
        moment.tz namespace
    ************************************/

    function tz () {
        var args = Array.prototype.slice.call(arguments, 0, -1),
            name = arguments[arguments.length - 1],
            zone = getZone(name),
            out  = moment.utc.apply(null, args);

        if (zone && needsOffset(out)) {
            out.add(zone.parse(out), 'minutes');
        }

        out.tz(name);

        return out;
    }

    tz.version      = VERSION;
    tz.dataVersion  = '';
    tz._zones       = zones;
    tz._links       = links;
    tz.add          = addZone;
    tz.link         = addLink;
    tz.load         = loadData;
    tz.zone         = getZone;
    tz.zoneExists   = zoneExists; // deprecated in 0.1.0
    tz.names        = getNames;
    tz.Zone         = Zone;
    tz.unpack       = unpack;
    tz.unpackBase60 = unpackBase60;
    tz.needsOffset  = needsOffset;
    tz.moveInvalidForward   = true;
    tz.moveAmbiguousForward = false;

    /************************************
        Interface with Moment.js
    ************************************/

    var fn = moment.fn;

    moment.tz = tz;

    moment.updateOffset = function (mom, keepTime) {
        var offset;
        if (mom._z) {
            offset = mom._z.offset(mom);
            if (Math.abs(offset) < 16) {
                offset = offset / 60;
            }
            mom.zone(offset, keepTime);
        }
    };

    fn.tz = function (name) {
        if (name) {
            this._z = getZone(name);
            if (this._z) {
                moment.updateOffset(this);
            } else {
                logError("Moment Timezone has no data for " + name + ". See http://momentjs.com/timezone/docs/#/data-loading/.");
            }
            return this;
        }
        if (this._z) { return this._z.name; }
    };

    function abbrWrap (old) {
        return function () {
            if (this._z) { return this._z.abbr(this); }
            return old.call(this);
        };
    }

    function resetZoneWrap (old) {
        return function () {
            this._z = null;
            return old.apply(this, arguments);
        };
    }

    fn.zoneName = abbrWrap(fn.zoneName);
    fn.zoneAbbr = abbrWrap(fn.zoneAbbr);
    fn.utc      = resetZoneWrap(fn.utc);

    // Cloning a moment should include the _z property.
    var momentProperties = moment.momentProperties;
    if (Object.prototype.toString.call(momentProperties) === '[object Array]') {
        // moment 2.8.1+
        momentProperties.push('_z');
        momentProperties.push('_a');
    } else if (momentProperties) {
        // moment 2.7.0
        momentProperties._z = null;
    }

    loadData({
        "version": "2014g",
        "zones": [
            "Africa/Abidjan|GMT|0|0|",
            "Africa/Addis_Ababa|EAT|-30|0|",
            "Africa/Algiers|CET|-10|0|",
            "Africa/Bangui|WAT|-10|0|",
            "Africa/Blantyre|CAT|-20|0|",
            "Africa/Cairo|EET EEST|-20 -30|0101010101010101010101010101010|1Cby0 Fb0 c10 8n0 8Nd0 gL0 e10 mn0 1o10 jz0 gN0 pb0 1qN0 dX0 e10 xz0 1o10 bb0 e10 An0 1o10 5z0 e10 FX0 1o10 2L0 e10 IL0 1C10 Lz0",
            "Africa/Casablanca|WET WEST|0 -10|01010101010101010101010101010101010101010|1Cco0 Db0 1zd0 Lz0 1Nf0 wM0 co0 go0 1o00 s00 dA0 vc0 11A0 A00 e00 y00 11A0 uo0 e00 DA0 11A0 rA0 e00 Jc0 WM0 m00 gM0 M00 WM0 jc0 e00 RA0 11A0 dA0 e00 Uo0 11A0 800 gM0 Xc0",
            "Africa/Ceuta|CET CEST|-10 -20|01010101010101010101010|1BWp0 1qM0 WM0 1qM0 WM0 1qM0 11A0 1o00 11A0 1o00 11A0 1o00 11A0 1qM0 WM0 1qM0 WM0 1qM0 11A0 1o00 11A0 1o00",
            "Africa/Johannesburg|SAST|-20|0|",
            "Africa/Tripoli|EET CET CEST|-20 -10 -20|0120|1IlA0 TA0 1o00",
            "Africa/Windhoek|WAST WAT|-20 -10|01010101010101010101010|1C1c0 11B0 1nX0 11B0 1nX0 11B0 1qL0 WN0 1qL0 11B0 1nX0 11B0 1nX0 11B0 1nX0 11B0 1nX0 11B0 1qL0 WN0 1qL0 11B0",
            "America/Adak|HAST HADT|a0 90|01010101010101010101010|1BR00 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0",
            "America/Anchorage|AKST AKDT|90 80|01010101010101010101010|1BQX0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0",
            "America/Anguilla|AST|40|0|",
            "America/Araguaina|BRT BRST|30 20|010|1IdD0 Lz0",
            "America/Argentina/Buenos_Aires|ART|30|0|",
            "America/Asuncion|PYST PYT|30 40|01010101010101010101010|1C430 1a10 1fz0 1a10 1fz0 1cN0 17b0 1ip0 17b0 1ip0 17b0 1ip0 19X0 1fB0 19X0 1fB0 19X0 1ip0 17b0 1ip0 17b0 1ip0",
            "America/Atikokan|EST|50|0|",
            "America/Bahia|BRT BRST|30 20|010|1FJf0 Rb0",
            "America/Bahia_Banderas|MST CDT CST|70 50 60|01212121212121212121212|1C1l0 1nW0 11B0 1nX0 11B0 1nX0 14p0 1lb0 14p0 1lb0 14p0 1lb0 14p0 1nX0 11B0 1nX0 11B0 1nX0 14p0 1lb0 14p0 1lb0",
            "America/Belem|BRT|30|0|",
            "America/Belize|CST|60|0|",
            "America/Boa_Vista|AMT|40|0|",
            "America/Bogota|COT|50|0|",
            "America/Boise|MST MDT|70 60|01010101010101010101010|1BQV0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0",
            "America/Campo_Grande|AMST AMT|30 40|01010101010101010101010|1BIr0 1zd0 On0 1zd0 Rb0 1zd0 Lz0 1C10 Lz0 1C10 On0 1zd0 On0 1zd0 On0 1zd0 On0 1C10 Lz0 1C10 Lz0 1C10",
            "America/Cancun|CST CDT|60 50|01010101010101010101010|1C1k0 1nX0 11B0 1nX0 11B0 1nX0 14p0 1lb0 14p0 1lb0 14p0 1lb0 14p0 1nX0 11B0 1nX0 11B0 1nX0 14p0 1lb0 14p0 1lb0",
            "America/Caracas|VET|4u|0|",
            "America/Cayenne|GFT|30|0|",
            "America/Chicago|CST CDT|60 50|01010101010101010101010|1BQU0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0",
            "America/Chihuahua|MST MDT|70 60|01010101010101010101010|1C1l0 1nX0 11B0 1nX0 11B0 1nX0 14p0 1lb0 14p0 1lb0 14p0 1lb0 14p0 1nX0 11B0 1nX0 11B0 1nX0 14p0 1lb0 14p0 1lb0",
            "America/Creston|MST|70|0|",
            "America/Dawson|PST PDT|80 70|01010101010101010101010|1BQW0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0",
            "America/Detroit|EST EDT|50 40|01010101010101010101010|1BQT0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0",
            "America/Eirunepe|AMT ACT|40 50|01|1KLE0",
            "America/Glace_Bay|AST ADT|40 30|01010101010101010101010|1BQS0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0",
            "America/Godthab|WGT WGST|30 20|01010101010101010101010|1BWp0 1qM0 WM0 1qM0 WM0 1qM0 11A0 1o00 11A0 1o00 11A0 1o00 11A0 1qM0 WM0 1qM0 WM0 1qM0 11A0 1o00 11A0 1o00",
            "America/Goose_Bay|AST ADT|40 30|01010101010101010101010|1BQQ1 1zb0 Op0 1zcX Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0",
            "America/Grand_Turk|EST EDT AST|50 40 40|01010101012|1BQT0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0",
            "America/Guayaquil|ECT|50|0|",
            "America/Guyana|GYT|40|0|",
            "America/Havana|CST CDT|50 40|01010101010101010101010|1BQR0 1wo0 U00 1zc0 U00 1qM0 Oo0 1zc0 Oo0 1zc0 Oo0 1zc0 Rc0 1zc0 Oo0 1zc0 Oo0 1zc0 Oo0 1zc0 Oo0 1zc0",
            "America/La_Paz|BOT|40|0|",
            "America/Lima|PET|50|0|",
            "America/Metlakatla|PST|80|0|",
            "America/Miquelon|PMST PMDT|30 20|01010101010101010101010|1BQR0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0",
            "America/Montevideo|UYST UYT|20 30|01010101010101010101010|1BQQ0 1ld0 14n0 1ld0 14n0 1o10 11z0 1o10 11z0 1o10 11z0 1o10 14n0 1ld0 14n0 1ld0 14n0 1o10 11z0 1o10 11z0 1o10",
            "America/Noronha|FNT|20|0|",
            "America/North_Dakota/Beulah|MST MDT CST CDT|70 60 60 50|01232323232323232323232|1BQV0 1zb0 Oo0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0",
            "America/Paramaribo|SRT|30|0|",
            "America/Port-au-Prince|EST EDT|50 40|0101010101010101010|1GI70 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0",
            "America/Santa_Isabel|PST PDT|80 70|01010101010101010101010|1C1m0 1nX0 11B0 1nX0 11B0 1nX0 14p0 1lb0 14p0 1lb0 14p0 1lb0 14p0 1nX0 11B0 1nX0 11B0 1nX0 14p0 1lb0 14p0 1lb0",
            "America/Santiago|CLST CLT|30 40|01010101010101010101010|1C1f0 1fB0 1nX0 G10 1EL0 Op0 1zb0 Rd0 1wn0 Rd0 1wn0 Rd0 1wn0 Rd0 1wn0 Rd0 1zb0 Op0 1zb0 Rd0 1wn0 Rd0",
            "America/Sao_Paulo|BRST BRT|20 30|01010101010101010101010|1BIq0 1zd0 On0 1zd0 Rb0 1zd0 Lz0 1C10 Lz0 1C10 On0 1zd0 On0 1zd0 On0 1zd0 On0 1C10 Lz0 1C10 Lz0 1C10",
            "America/Scoresbysund|EGT EGST|10 0|01010101010101010101010|1BWp0 1qM0 WM0 1qM0 WM0 1qM0 11A0 1o00 11A0 1o00 11A0 1o00 11A0 1qM0 WM0 1qM0 WM0 1qM0 11A0 1o00 11A0 1o00",
            "America/St_Johns|NST NDT|3u 2u|01010101010101010101010|1BQPv 1zb0 Op0 1zcX Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0",
            "Antarctica/Casey|CAST AWST|-b0 -80|0101|1BN30 40P0 KL0",
            "Antarctica/Davis|DAVT DAVT|-50 -70|0101|1BPw0 3Wn0 KN0",
            "Antarctica/DumontDUrville|DDUT|-a0|0|",
            "Antarctica/Macquarie|AEDT MIST|-b0 -b0|01|1C140",
            "Antarctica/Mawson|MAWT|-50|0|",
            "Antarctica/McMurdo|NZDT NZST|-d0 -c0|01010101010101010101010|1C120 1a00 1fA0 1a00 1fA0 1cM0 1fA0 1a00 1fA0 1a00 1fA0 1a00 1fA0 1a00 1fA0 1a00 1fA0 1cM0 1fA0 1a00 1fA0 1a00",
            "Antarctica/Rothera|ROTT|30|0|",
            "Antarctica/Syowa|SYOT|-30|0|",
            "Antarctica/Troll|UTC CEST|0 -20|01010101010101010101010|1BWp0 1qM0 WM0 1qM0 WM0 1qM0 11A0 1o00 11A0 1o00 11A0 1o00 11A0 1qM0 WM0 1qM0 WM0 1qM0 11A0 1o00 11A0 1o00",
            "Antarctica/Vostok|VOST|-60|0|",
            "Asia/Aden|AST|-30|0|",
            "Asia/Almaty|ALMT|-60|0|",
            "Asia/Amman|EET EEST|-20 -30|010101010101010101010|1BVy0 1qM0 11A0 1o00 11A0 4bX0 Dd0 1qM0 WM0 1qM0 11A0 1o00 11A0 1o00 11A0 1o00 11A0 1o00 11A0 1qM0",
            "Asia/Anadyr|ANAT ANAST ANAT|-c0 -c0 -b0|0120|1BWe0 1qN0 WM0",
            "Asia/Aqtau|AQTT|-50|0|",
            "Asia/Ashgabat|TMT|-50|0|",
            "Asia/Baku|AZT AZST|-40 -50|01010101010101010101010|1BWo0 1qM0 WM0 1qM0 WM0 1qM0 11A0 1o00 11A0 1o00 11A0 1o00 11A0 1qM0 WM0 1qM0 WM0 1qM0 11A0 1o00 11A0 1o00",
            "Asia/Bangkok|ICT|-70|0|",
            "Asia/Beirut|EET EEST|-20 -30|01010101010101010101010|1BWm0 1qL0 WN0 1qL0 WN0 1qL0 11B0 1nX0 11B0 1nX0 11B0 1nX0 11B0 1qL0 WN0 1qL0 WN0 1qL0 11B0 1nX0 11B0 1nX0",
            "Asia/Bishkek|KGT|-60|0|",
            "Asia/Brunei|BNT|-80|0|",
            "Asia/Calcutta|IST|-5u|0|",
            "Asia/Chita|YAKT YAKST YAKT IRKT|-90 -a0 -a0 -80|01023|1BWh0 1qM0 WM0 8Hz0",
            "Asia/Choibalsan|CHOT|-80|0|",
            "Asia/Chongqing|CST|-80|0|",
            "Asia/Dacca|BDT|-60|0|",
            "Asia/Damascus|EET EEST|-20 -30|01010101010101010101010|1C0m0 1nX0 11B0 1nX0 11B0 1nX0 11B0 1nX0 11B0 1qL0 WN0 1qL0 WN0 1qL0 11B0 1nX0 11B0 1nX0 11B0 1nX0 11B0 1qL0",
            "Asia/Dili|TLT|-90|0|",
            "Asia/Dubai|GST|-40|0|",
            "Asia/Dushanbe|TJT|-50|0|",
            "Asia/Gaza|EET EEST|-20 -30|01010101010101010101010|1BVW1 SKX 1xd1 MKX 1AN0 1a00 1fA0 1cL0 1cN0 1cL0 1cN0 1cL0 1fB0 19X0 1fB0 19X0 1fB0 19X0 1fB0 1cL0 1cN0 1cL0",
            "Asia/Hebron|EET EEST|-20 -30|0101010101010101010101010|1BVy0 Tb0 1xd1 MKX bB0 cn0 1cN0 1a00 1fA0 1cL0 1cN0 1cL0 1cN0 1cL0 1fB0 19X0 1fB0 19X0 1fB0 19X0 1fB0 1cL0 1cN0 1cL0",
            "Asia/Hong_Kong|HKT|-80|0|",
            "Asia/Hovd|HOVT|-70|0|",
            "Asia/Irkutsk|IRKT IRKST IRKT|-80 -90 -90|01020|1BWi0 1qM0 WM0 8Hz0",
            "Asia/Istanbul|EET EEST|-20 -30|01010101010101010101010|1BWp0 1qM0 Xc0 1qo0 WM0 1qM0 11A0 1o00 1200 1nA0 11A0 1o00 11A0 1qM0 WM0 1qM0 WM0 1qM0 11A0 1o00 11A0 1o00",
            "Asia/Jakarta|WIB|-70|0|",
            "Asia/Jayapura|WIT|-90|0|",
            "Asia/Jerusalem|IST IDT|-20 -30|01010101010101010101010|1BVA0 17X0 1kp0 1dz0 1c10 1aL0 1eN0 1oL0 10N0 1oL0 10N0 1oL0 10N0 1rz0 W10 1rz0 W10 1rz0 10N0 1oL0 10N0 1oL0",
            "Asia/Kabul|AFT|-4u|0|",
            "Asia/Kamchatka|PETT PETST PETT|-c0 -c0 -b0|0120|1BWe0 1qN0 WM0",
            "Asia/Karachi|PKT|-50|0|",
            "Asia/Kashgar|XJT|-60|0|",
            "Asia/Kathmandu|NPT|-5J|0|",
            "Asia/Khandyga|VLAT VLAST VLAT YAKT YAKT|-a0 -b0 -b0 -a0 -90|010234|1BWg0 1qM0 WM0 17V0 7zD0",
            "Asia/Krasnoyarsk|KRAT KRAST KRAT|-70 -80 -80|01020|1BWj0 1qM0 WM0 8Hz0",
            "Asia/Kuala_Lumpur|MYT|-80|0|",
            "Asia/Magadan|MAGT MAGST MAGT MAGT|-b0 -c0 -c0 -a0|01023|1BWf0 1qM0 WM0 8Hz0",
            "Asia/Makassar|WITA|-80|0|",
            "Asia/Manila|PHT|-80|0|",
            "Asia/Nicosia|EET EEST|-20 -30|01010101010101010101010|1BWp0 1qM0 WM0 1qM0 WM0 1qM0 11A0 1o00 11A0 1o00 11A0 1o00 11A0 1qM0 WM0 1qM0 WM0 1qM0 11A0 1o00 11A0 1o00",
            "Asia/Novokuznetsk|KRAT NOVST NOVT NOVT|-70 -70 -60 -70|01230|1BWj0 1qN0 WM0 8Hz0",
            "Asia/Novosibirsk|NOVT NOVST NOVT|-60 -70 -70|01020|1BWk0 1qM0 WM0 8Hz0",
            "Asia/Omsk|OMST OMSST OMST|-60 -70 -70|01020|1BWk0 1qM0 WM0 8Hz0",
            "Asia/Oral|ORAT|-50|0|",
            "Asia/Pyongyang|KST|-90|0|",
            "Asia/Qyzylorda|QYZT|-60|0|",
            "Asia/Rangoon|MMT|-6u|0|",
            "Asia/Sakhalin|SAKT SAKST SAKT|-a0 -b0 -b0|01020|1BWg0 1qM0 WM0 8Hz0",
            "Asia/Samarkand|UZT|-50|0|",
            "Asia/Singapore|SGT|-80|0|",
            "Asia/Srednekolymsk|MAGT MAGST MAGT SRET|-b0 -c0 -c0 -b0|01023|1BWf0 1qM0 WM0 8Hz0",
            "Asia/Tbilisi|GET|-40|0|",
            "Asia/Tehran|IRST IRDT|-3u -4u|01010101010101010101010|1BTUu 1dz0 1cp0 1dz0 1cp0 1dz0 1cN0 1dz0 1cp0 1dz0 1cp0 1dz0 1cp0 1dz0 1cN0 1dz0 1cp0 1dz0 1cp0 1dz0 1cp0 1dz0",
            "Asia/Thimbu|BTT|-60|0|",
            "Asia/Tokyo|JST|-90|0|",
            "Asia/Ulaanbaatar|ULAT|-80|0|",
            "Asia/Ust-Nera|MAGT MAGST MAGT VLAT VLAT|-b0 -c0 -c0 -b0 -a0|010234|1BWf0 1qM0 WM0 17V0 7zD0",
            "Asia/Vladivostok|VLAT VLAST VLAT|-a0 -b0 -b0|01020|1BWg0 1qM0 WM0 8Hz0",
            "Asia/Yakutsk|YAKT YAKST YAKT|-90 -a0 -a0|01020|1BWh0 1qM0 WM0 8Hz0",
            "Asia/Yekaterinburg|YEKT YEKST YEKT|-50 -60 -60|01020|1BWl0 1qM0 WM0 8Hz0",
            "Asia/Yerevan|AMT AMST|-40 -50|01010|1BWm0 1qM0 WM0 1qM0",
            "Atlantic/Azores|AZOT AZOST|10 0|01010101010101010101010|1BWp0 1qM0 WM0 1qM0 WM0 1qM0 11A0 1o00 11A0 1o00 11A0 1o00 11A0 1qM0 WM0 1qM0 WM0 1qM0 11A0 1o00 11A0 1o00",
            "Atlantic/Canary|WET WEST|0 -10|01010101010101010101010|1BWp0 1qM0 WM0 1qM0 WM0 1qM0 11A0 1o00 11A0 1o00 11A0 1o00 11A0 1qM0 WM0 1qM0 WM0 1qM0 11A0 1o00 11A0 1o00",
            "Atlantic/Cape_Verde|CVT|10|0|",
            "Atlantic/South_Georgia|GST|20|0|",
            "Atlantic/Stanley|FKST FKT|30 40|010|1C6R0 U10",
            "Australia/ACT|AEDT AEST|-b0 -a0|01010101010101010101010|1C140 1cM0 1cM0 1cM0 1cM0 1fA0 1cM0 1cM0 1cM0 1cM0 1cM0 1cM0 1cM0 1cM0 1cM0 1cM0 1cM0 1fA0 1cM0 1cM0 1cM0 1cM0",
            "Australia/Adelaide|ACDT ACST|-au -9u|01010101010101010101010|1C14u 1cM0 1cM0 1cM0 1cM0 1fA0 1cM0 1cM0 1cM0 1cM0 1cM0 1cM0 1cM0 1cM0 1cM0 1cM0 1cM0 1fA0 1cM0 1cM0 1cM0 1cM0",
            "Australia/Brisbane|AEST|-a0|0|",
            "Australia/Darwin|ACST|-9u|0|",
            "Australia/Eucla|ACWST|-8J|0|",
            "Australia/LHI|LHDT LHST|-b0 -au|01010101010101010101010|1C130 1cMu 1cLu 1cMu 1cLu 1fAu 1cLu 1cMu 1cLu 1cMu 1cLu 1cMu 1cLu 1cMu 1cLu 1cMu 1cLu 1fAu 1cLu 1cMu 1cLu 1cMu",
            "Australia/Perth|AWST|-80|0|",
            "Chile/EasterIsland|EASST EAST|50 60|01010101010101010101010|1C1f0 1fB0 1nX0 G10 1EL0 Op0 1zb0 Rd0 1wn0 Rd0 1wn0 Rd0 1wn0 Rd0 1wn0 Rd0 1zb0 Op0 1zb0 Rd0 1wn0 Rd0",
            "Eire|GMT IST|0 -10|01010101010101010101010|1BWp0 1qM0 WM0 1qM0 WM0 1qM0 11A0 1o00 11A0 1o00 11A0 1o00 11A0 1qM0 WM0 1qM0 WM0 1qM0 11A0 1o00 11A0 1o00",
            "Etc/GMT+1|GMT+1|10|0|",
            "Etc/GMT+10|GMT+10|a0|0|",
            "Etc/GMT+11|GMT+11|b0|0|",
            "Etc/GMT+12|GMT+12|c0|0|",
            "Etc/GMT+2|GMT+2|20|0|",
            "Etc/GMT+3|GMT+3|30|0|",
            "Etc/GMT+4|GMT+4|40|0|",
            "Etc/GMT+5|GMT+5|50|0|",
            "Etc/GMT+6|GMT+6|60|0|",
            "Etc/GMT+7|GMT+7|70|0|",
            "Etc/GMT+8|GMT+8|80|0|",
            "Etc/GMT+9|GMT+9|90|0|",
            "Etc/GMT-1|GMT-1|-10|0|",
            "Etc/GMT-10|GMT-10|-a0|0|",
            "Etc/GMT-11|GMT-11|-b0|0|",
            "Etc/GMT-12|GMT-12|-c0|0|",
            "Etc/GMT-13|GMT-13|-d0|0|",
            "Etc/GMT-14|GMT-14|-e0|0|",
            "Etc/GMT-2|GMT-2|-20|0|",
            "Etc/GMT-3|GMT-3|-30|0|",
            "Etc/GMT-4|GMT-4|-40|0|",
            "Etc/GMT-5|GMT-5|-50|0|",
            "Etc/GMT-6|GMT-6|-60|0|",
            "Etc/GMT-7|GMT-7|-70|0|",
            "Etc/GMT-8|GMT-8|-80|0|",
            "Etc/GMT-9|GMT-9|-90|0|",
            "Etc/UCT|UCT|0|0|",
            "Etc/UTC|UTC|0|0|",
            "Europe/Belfast|GMT BST|0 -10|01010101010101010101010|1BWp0 1qM0 WM0 1qM0 WM0 1qM0 11A0 1o00 11A0 1o00 11A0 1o00 11A0 1qM0 WM0 1qM0 WM0 1qM0 11A0 1o00 11A0 1o00",
            "Europe/Kaliningrad|EET EEST FET|-20 -30 -30|01020|1BWo0 1qM0 WM0 8Hz0",
            "Europe/Minsk|EET EEST FET|-20 -30 -30|0102|1BWo0 1qM0 WM0",
            "Europe/Moscow|MSK MSD MSK|-30 -40 -40|01020|1BWn0 1qM0 WM0 8Hz0",
            "Europe/Samara|SAMT SAMST SAMT|-40 -40 -30|0120|1BWm0 1qN0 WM0",
            "Europe/Simferopol|EET EEST MSK MSK|-20 -30 -40 -30|01010101023|1BWp0 1qM0 WM0 1qM0 WM0 1qM0 11A0 1o00 11z0 1nW0",
            "Europe/Volgograd|MSK MSK|-30 -40|01010|1BWn0 1qM0 WM0 8Hz0",
            "HST|HST|a0|0|",
            "Indian/Chagos|IOT|-60|0|",
            "Indian/Christmas|CXT|-70|0|",
            "Indian/Cocos|CCT|-6u|0|",
            "Indian/Kerguelen|TFT|-50|0|",
            "Indian/Mahe|SCT|-40|0|",
            "Indian/Maldives|MVT|-50|0|",
            "Indian/Mauritius|MUT|-40|0|",
            "Indian/Reunion|RET|-40|0|",
            "Kwajalein|MHT|-c0|0|",
            "MET|MET MEST|-10 -20|01010101010101010101010|1BWp0 1qM0 WM0 1qM0 WM0 1qM0 11A0 1o00 11A0 1o00 11A0 1o00 11A0 1qM0 WM0 1qM0 WM0 1qM0 11A0 1o00 11A0 1o00",
            "NZ-CHAT|CHADT CHAST|-dJ -cJ|01010101010101010101010|1C120 1a00 1fA0 1a00 1fA0 1cM0 1fA0 1a00 1fA0 1a00 1fA0 1a00 1fA0 1a00 1fA0 1a00 1fA0 1cM0 1fA0 1a00 1fA0 1a00",
            "Pacific/Apia|SST SDT WSDT WSST|b0 a0 -e0 -d0|01012323232323232323232|1Dbn0 1ff0 1a00 CI0 AQ0 1cM0 1fA0 1a00 1fA0 1a00 1fA0 1a00 1fA0 1a00 1fA0 1a00 1fA0 1cM0 1fA0 1a00 1fA0 1a00",
            "Pacific/Chuuk|CHUT|-a0|0|",
            "Pacific/Efate|VUT|-b0|0|",
            "Pacific/Enderbury|PHOT|-d0|0|",
            "Pacific/Fakaofo|TKT TKT|b0 -d0|01|1Gfn0",
            "Pacific/Fiji|FJST FJT|-d0 -c0|01010101010101010101010|1BWe0 1o00 Rc0 1wo0 Ao0 1Nc0 Ao0 1Q00 xz0 1Q10 xz0 1Q10 An0 1Nd0 An0 1Nd0 An0 1Nd0 An0 1Q10 xz0 1Q10",
            "Pacific/Funafuti|TVT|-c0|0|",
            "Pacific/Galapagos|GALT|60|0|",
            "Pacific/Gambier|GAMT|90|0|",
            "Pacific/Guadalcanal|SBT|-b0|0|",
            "Pacific/Guam|ChST|-a0|0|",
            "Pacific/Kiritimati|LINT|-e0|0|",
            "Pacific/Kosrae|KOST|-b0|0|",
            "Pacific/Marquesas|MART|9u|0|",
            "Pacific/Midway|SST|b0|0|",
            "Pacific/Nauru|NRT|-c0|0|",
            "Pacific/Niue|NUT|b0|0|",
            "Pacific/Norfolk|NFT|-bu|0|",
            "Pacific/Noumea|NCT|-b0|0|",
            "Pacific/Palau|PWT|-90|0|",
            "Pacific/Pohnpei|PONT|-b0|0|",
            "Pacific/Port_Moresby|PGT|-a0|0|",
            "Pacific/Rarotonga|CKT|a0|0|",
            "Pacific/Tahiti|TAHT|a0|0|",
            "Pacific/Tarawa|GILT|-c0|0|",
            "Pacific/Tongatapu|TOT|-d0|0|",
            "Pacific/Wake|WAKT|-c0|0|",
            "Pacific/Wallis|WFT|-c0|0|"
        ],
        "links": [
            "Africa/Abidjan|Africa/Accra",
            "Africa/Abidjan|Africa/Bamako",
            "Africa/Abidjan|Africa/Banjul",
            "Africa/Abidjan|Africa/Bissau",
            "Africa/Abidjan|Africa/Conakry",
            "Africa/Abidjan|Africa/Dakar",
            "Africa/Abidjan|Africa/Freetown",
            "Africa/Abidjan|Africa/Lome",
            "Africa/Abidjan|Africa/Monrovia",
            "Africa/Abidjan|Africa/Nouakchott",
            "Africa/Abidjan|Africa/Ouagadougou",
            "Africa/Abidjan|Africa/Sao_Tome",
            "Africa/Abidjan|Africa/Timbuktu",
            "Africa/Abidjan|America/Danmarkshavn",
            "Africa/Abidjan|Atlantic/Reykjavik",
            "Africa/Abidjan|Atlantic/St_Helena",
            "Africa/Abidjan|Etc/GMT",
            "Africa/Abidjan|Etc/GMT+0",
            "Africa/Abidjan|Etc/GMT-0",
            "Africa/Abidjan|Etc/GMT0",
            "Africa/Abidjan|Etc/Greenwich",
            "Africa/Abidjan|GMT",
            "Africa/Abidjan|GMT+0",
            "Africa/Abidjan|GMT-0",
            "Africa/Abidjan|GMT0",
            "Africa/Abidjan|Greenwich",
            "Africa/Abidjan|Iceland",
            "Africa/Addis_Ababa|Africa/Asmara",
            "Africa/Addis_Ababa|Africa/Asmera",
            "Africa/Addis_Ababa|Africa/Dar_es_Salaam",
            "Africa/Addis_Ababa|Africa/Djibouti",
            "Africa/Addis_Ababa|Africa/Juba",
            "Africa/Addis_Ababa|Africa/Kampala",
            "Africa/Addis_Ababa|Africa/Khartoum",
            "Africa/Addis_Ababa|Africa/Mogadishu",
            "Africa/Addis_Ababa|Africa/Nairobi",
            "Africa/Addis_Ababa|Indian/Antananarivo",
            "Africa/Addis_Ababa|Indian/Comoro",
            "Africa/Addis_Ababa|Indian/Mayotte",
            "Africa/Algiers|Africa/Tunis",
            "Africa/Bangui|Africa/Brazzaville",
            "Africa/Bangui|Africa/Douala",
            "Africa/Bangui|Africa/Kinshasa",
            "Africa/Bangui|Africa/Lagos",
            "Africa/Bangui|Africa/Libreville",
            "Africa/Bangui|Africa/Luanda",
            "Africa/Bangui|Africa/Malabo",
            "Africa/Bangui|Africa/Ndjamena",
            "Africa/Bangui|Africa/Niamey",
            "Africa/Bangui|Africa/Porto-Novo",
            "Africa/Blantyre|Africa/Bujumbura",
            "Africa/Blantyre|Africa/Gaborone",
            "Africa/Blantyre|Africa/Harare",
            "Africa/Blantyre|Africa/Kigali",
            "Africa/Blantyre|Africa/Lubumbashi",
            "Africa/Blantyre|Africa/Lusaka",
            "Africa/Blantyre|Africa/Maputo",
            "Africa/Cairo|Egypt",
            "Africa/Casablanca|Africa/El_Aaiun",
            "Africa/Ceuta|Arctic/Longyearbyen",
            "Africa/Ceuta|Atlantic/Jan_Mayen",
            "Africa/Ceuta|CET",
            "Africa/Ceuta|Europe/Amsterdam",
            "Africa/Ceuta|Europe/Andorra",
            "Africa/Ceuta|Europe/Belgrade",
            "Africa/Ceuta|Europe/Berlin",
            "Africa/Ceuta|Europe/Bratislava",
            "Africa/Ceuta|Europe/Brussels",
            "Africa/Ceuta|Europe/Budapest",
            "Africa/Ceuta|Europe/Busingen",
            "Africa/Ceuta|Europe/Copenhagen",
            "Africa/Ceuta|Europe/Gibraltar",
            "Africa/Ceuta|Europe/Ljubljana",
            "Africa/Ceuta|Europe/Luxembourg",
            "Africa/Ceuta|Europe/Madrid",
            "Africa/Ceuta|Europe/Malta",
            "Africa/Ceuta|Europe/Monaco",
            "Africa/Ceuta|Europe/Oslo",
            "Africa/Ceuta|Europe/Paris",
            "Africa/Ceuta|Europe/Podgorica",
            "Africa/Ceuta|Europe/Prague",
            "Africa/Ceuta|Europe/Rome",
            "Africa/Ceuta|Europe/San_Marino",
            "Africa/Ceuta|Europe/Sarajevo",
            "Africa/Ceuta|Europe/Skopje",
            "Africa/Ceuta|Europe/Stockholm",
            "Africa/Ceuta|Europe/Tirane",
            "Africa/Ceuta|Europe/Vaduz",
            "Africa/Ceuta|Europe/Vatican",
            "Africa/Ceuta|Europe/Vienna",
            "Africa/Ceuta|Europe/Warsaw",
            "Africa/Ceuta|Europe/Zagreb",
            "Africa/Ceuta|Europe/Zurich",
            "Africa/Ceuta|Poland",
            "Africa/Johannesburg|Africa/Maseru",
            "Africa/Johannesburg|Africa/Mbabane",
            "Africa/Tripoli|Libya",
            "America/Adak|America/Atka",
            "America/Adak|US/Aleutian",
            "America/Anchorage|America/Juneau",
            "America/Anchorage|America/Nome",
            "America/Anchorage|America/Sitka",
            "America/Anchorage|America/Yakutat",
            "America/Anchorage|US/Alaska",
            "America/Anguilla|America/Antigua",
            "America/Anguilla|America/Aruba",
            "America/Anguilla|America/Barbados",
            "America/Anguilla|America/Blanc-Sablon",
            "America/Anguilla|America/Curacao",
            "America/Anguilla|America/Dominica",
            "America/Anguilla|America/Grenada",
            "America/Anguilla|America/Guadeloupe",
            "America/Anguilla|America/Kralendijk",
            "America/Anguilla|America/Lower_Princes",
            "America/Anguilla|America/Marigot",
            "America/Anguilla|America/Martinique",
            "America/Anguilla|America/Montserrat",
            "America/Anguilla|America/Port_of_Spain",
            "America/Anguilla|America/Puerto_Rico",
            "America/Anguilla|America/Santo_Domingo",
            "America/Anguilla|America/St_Barthelemy",
            "America/Anguilla|America/St_Kitts",
            "America/Anguilla|America/St_Lucia",
            "America/Anguilla|America/St_Thomas",
            "America/Anguilla|America/St_Vincent",
            "America/Anguilla|America/Tortola",
            "America/Anguilla|America/Virgin",
            "America/Argentina/Buenos_Aires|America/Argentina/Catamarca",
            "America/Argentina/Buenos_Aires|America/Argentina/ComodRivadavia",
            "America/Argentina/Buenos_Aires|America/Argentina/Cordoba",
            "America/Argentina/Buenos_Aires|America/Argentina/Jujuy",
            "America/Argentina/Buenos_Aires|America/Argentina/La_Rioja",
            "America/Argentina/Buenos_Aires|America/Argentina/Mendoza",
            "America/Argentina/Buenos_Aires|America/Argentina/Rio_Gallegos",
            "America/Argentina/Buenos_Aires|America/Argentina/Salta",
            "America/Argentina/Buenos_Aires|America/Argentina/San_Juan",
            "America/Argentina/Buenos_Aires|America/Argentina/San_Luis",
            "America/Argentina/Buenos_Aires|America/Argentina/Tucuman",
            "America/Argentina/Buenos_Aires|America/Argentina/Ushuaia",
            "America/Argentina/Buenos_Aires|America/Buenos_Aires",
            "America/Argentina/Buenos_Aires|America/Catamarca",
            "America/Argentina/Buenos_Aires|America/Cordoba",
            "America/Argentina/Buenos_Aires|America/Jujuy",
            "America/Argentina/Buenos_Aires|America/Mendoza",
            "America/Argentina/Buenos_Aires|America/Rosario",
            "America/Atikokan|America/Cayman",
            "America/Atikokan|America/Coral_Harbour",
            "America/Atikokan|America/Jamaica",
            "America/Atikokan|America/Panama",
            "America/Atikokan|EST",
            "America/Atikokan|Jamaica",
            "America/Belem|America/Fortaleza",
            "America/Belem|America/Maceio",
            "America/Belem|America/Recife",
            "America/Belem|America/Santarem",
            "America/Belize|America/Costa_Rica",
            "America/Belize|America/El_Salvador",
            "America/Belize|America/Guatemala",
            "America/Belize|America/Managua",
            "America/Belize|America/Regina",
            "America/Belize|America/Swift_Current",
            "America/Belize|America/Tegucigalpa",
            "America/Belize|Canada/East-Saskatchewan",
            "America/Belize|Canada/Saskatchewan",
            "America/Boa_Vista|America/Manaus",
            "America/Boa_Vista|America/Porto_Velho",
            "America/Boa_Vista|Brazil/West",
            "America/Boise|America/Cambridge_Bay",
            "America/Boise|America/Denver",
            "America/Boise|America/Edmonton",
            "America/Boise|America/Inuvik",
            "America/Boise|America/Ojinaga",
            "America/Boise|America/Shiprock",
            "America/Boise|America/Yellowknife",
            "America/Boise|Canada/Mountain",
            "America/Boise|MST7MDT",
            "America/Boise|Navajo",
            "America/Boise|US/Mountain",
            "America/Campo_Grande|America/Cuiaba",
            "America/Cancun|America/Merida",
            "America/Cancun|America/Mexico_City",
            "America/Cancun|America/Monterrey",
            "America/Cancun|Mexico/General",
            "America/Chicago|America/Indiana/Knox",
            "America/Chicago|America/Indiana/Tell_City",
            "America/Chicago|America/Knox_IN",
            "America/Chicago|America/Matamoros",
            "America/Chicago|America/Menominee",
            "America/Chicago|America/North_Dakota/Center",
            "America/Chicago|America/North_Dakota/New_Salem",
            "America/Chicago|America/Rainy_River",
            "America/Chicago|America/Rankin_Inlet",
            "America/Chicago|America/Resolute",
            "America/Chicago|America/Winnipeg",
            "America/Chicago|CST6CDT",
            "America/Chicago|Canada/Central",
            "America/Chicago|US/Central",
            "America/Chicago|US/Indiana-Starke",
            "America/Chihuahua|America/Mazatlan",
            "America/Chihuahua|Mexico/BajaSur",
            "America/Creston|America/Dawson_Creek",
            "America/Creston|America/Hermosillo",
            "America/Creston|America/Phoenix",
            "America/Creston|MST",
            "America/Creston|US/Arizona",
            "America/Dawson|America/Ensenada",
            "America/Dawson|America/Los_Angeles",
            "America/Dawson|America/Tijuana",
            "America/Dawson|America/Vancouver",
            "America/Dawson|America/Whitehorse",
            "America/Dawson|Canada/Pacific",
            "America/Dawson|Canada/Yukon",
            "America/Dawson|Mexico/BajaNorte",
            "America/Dawson|PST8PDT",
            "America/Dawson|US/Pacific",
            "America/Dawson|US/Pacific-New",
            "America/Detroit|America/Fort_Wayne",
            "America/Detroit|America/Indiana/Indianapolis",
            "America/Detroit|America/Indiana/Marengo",
            "America/Detroit|America/Indiana/Petersburg",
            "America/Detroit|America/Indiana/Vevay",
            "America/Detroit|America/Indiana/Vincennes",
            "America/Detroit|America/Indiana/Winamac",
            "America/Detroit|America/Indianapolis",
            "America/Detroit|America/Iqaluit",
            "America/Detroit|America/Kentucky/Louisville",
            "America/Detroit|America/Kentucky/Monticello",
            "America/Detroit|America/Louisville",
            "America/Detroit|America/Montreal",
            "America/Detroit|America/Nassau",
            "America/Detroit|America/New_York",
            "America/Detroit|America/Nipigon",
            "America/Detroit|America/Pangnirtung",
            "America/Detroit|America/Thunder_Bay",
            "America/Detroit|America/Toronto",
            "America/Detroit|Canada/Eastern",
            "America/Detroit|EST5EDT",
            "America/Detroit|US/East-Indiana",
            "America/Detroit|US/Eastern",
            "America/Detroit|US/Michigan",
            "America/Eirunepe|America/Porto_Acre",
            "America/Eirunepe|America/Rio_Branco",
            "America/Eirunepe|Brazil/Acre",
            "America/Glace_Bay|America/Halifax",
            "America/Glace_Bay|America/Moncton",
            "America/Glace_Bay|America/Thule",
            "America/Glace_Bay|Atlantic/Bermuda",
            "America/Glace_Bay|Canada/Atlantic",
            "America/Havana|Cuba",
            "America/Metlakatla|Pacific/Pitcairn",
            "America/Noronha|Brazil/DeNoronha",
            "America/Santiago|Antarctica/Palmer",
            "America/Santiago|Chile/Continental",
            "America/Sao_Paulo|Brazil/East",
            "America/St_Johns|Canada/Newfoundland",
            "Antarctica/McMurdo|Antarctica/South_Pole",
            "Antarctica/McMurdo|NZ",
            "Antarctica/McMurdo|Pacific/Auckland",
            "Asia/Aden|Asia/Baghdad",
            "Asia/Aden|Asia/Bahrain",
            "Asia/Aden|Asia/Kuwait",
            "Asia/Aden|Asia/Qatar",
            "Asia/Aden|Asia/Riyadh",
            "Asia/Aqtau|Asia/Aqtobe",
            "Asia/Ashgabat|Asia/Ashkhabad",
            "Asia/Bangkok|Asia/Ho_Chi_Minh",
            "Asia/Bangkok|Asia/Phnom_Penh",
            "Asia/Bangkok|Asia/Saigon",
            "Asia/Bangkok|Asia/Vientiane",
            "Asia/Calcutta|Asia/Colombo",
            "Asia/Calcutta|Asia/Kolkata",
            "Asia/Chongqing|Asia/Chungking",
            "Asia/Chongqing|Asia/Harbin",
            "Asia/Chongqing|Asia/Macao",
            "Asia/Chongqing|Asia/Macau",
            "Asia/Chongqing|Asia/Shanghai",
            "Asia/Chongqing|Asia/Taipei",
            "Asia/Chongqing|PRC",
            "Asia/Chongqing|ROC",
            "Asia/Dacca|Asia/Dhaka",
            "Asia/Dubai|Asia/Muscat",
            "Asia/Hong_Kong|Hongkong",
            "Asia/Istanbul|Europe/Istanbul",
            "Asia/Istanbul|Turkey",
            "Asia/Jakarta|Asia/Pontianak",
            "Asia/Jerusalem|Asia/Tel_Aviv",
            "Asia/Jerusalem|Israel",
            "Asia/Kashgar|Asia/Urumqi",
            "Asia/Kathmandu|Asia/Katmandu",
            "Asia/Kuala_Lumpur|Asia/Kuching",
            "Asia/Makassar|Asia/Ujung_Pandang",
            "Asia/Nicosia|EET",
            "Asia/Nicosia|Europe/Athens",
            "Asia/Nicosia|Europe/Bucharest",
            "Asia/Nicosia|Europe/Chisinau",
            "Asia/Nicosia|Europe/Helsinki",
            "Asia/Nicosia|Europe/Kiev",
            "Asia/Nicosia|Europe/Mariehamn",
            "Asia/Nicosia|Europe/Nicosia",
            "Asia/Nicosia|Europe/Riga",
            "Asia/Nicosia|Europe/Sofia",
            "Asia/Nicosia|Europe/Tallinn",
            "Asia/Nicosia|Europe/Tiraspol",
            "Asia/Nicosia|Europe/Uzhgorod",
            "Asia/Nicosia|Europe/Vilnius",
            "Asia/Nicosia|Europe/Zaporozhye",
            "Asia/Pyongyang|Asia/Seoul",
            "Asia/Pyongyang|ROK",
            "Asia/Samarkand|Asia/Tashkent",
            "Asia/Singapore|Singapore",
            "Asia/Tehran|Iran",
            "Asia/Thimbu|Asia/Thimphu",
            "Asia/Tokyo|Japan",
            "Asia/Ulaanbaatar|Asia/Ulan_Bator",
            "Atlantic/Canary|Atlantic/Faeroe",
            "Atlantic/Canary|Atlantic/Faroe",
            "Atlantic/Canary|Atlantic/Madeira",
            "Atlantic/Canary|Europe/Lisbon",
            "Atlantic/Canary|Portugal",
            "Atlantic/Canary|WET",
            "Australia/ACT|Australia/Canberra",
            "Australia/ACT|Australia/Currie",
            "Australia/ACT|Australia/Hobart",
            "Australia/ACT|Australia/Melbourne",
            "Australia/ACT|Australia/NSW",
            "Australia/ACT|Australia/Sydney",
            "Australia/ACT|Australia/Tasmania",
            "Australia/ACT|Australia/Victoria",
            "Australia/Adelaide|Australia/Broken_Hill",
            "Australia/Adelaide|Australia/South",
            "Australia/Adelaide|Australia/Yancowinna",
            "Australia/Brisbane|Australia/Lindeman",
            "Australia/Brisbane|Australia/Queensland",
            "Australia/Darwin|Australia/North",
            "Australia/LHI|Australia/Lord_Howe",
            "Australia/Perth|Australia/West",
            "Chile/EasterIsland|Pacific/Easter",
            "Eire|Europe/Dublin",
            "Etc/UCT|UCT",
            "Etc/UTC|Etc/Universal",
            "Etc/UTC|Etc/Zulu",
            "Etc/UTC|UTC",
            "Etc/UTC|Universal",
            "Etc/UTC|Zulu",
            "Europe/Belfast|Europe/Guernsey",
            "Europe/Belfast|Europe/Isle_of_Man",
            "Europe/Belfast|Europe/Jersey",
            "Europe/Belfast|Europe/London",
            "Europe/Belfast|GB",
            "Europe/Belfast|GB-Eire",
            "Europe/Moscow|W-SU",
            "HST|Pacific/Honolulu",
            "HST|Pacific/Johnston",
            "HST|US/Hawaii",
            "Kwajalein|Pacific/Kwajalein",
            "Kwajalein|Pacific/Majuro",
            "NZ-CHAT|Pacific/Chatham",
            "Pacific/Chuuk|Pacific/Truk",
            "Pacific/Chuuk|Pacific/Yap",
            "Pacific/Guam|Pacific/Saipan",
            "Pacific/Midway|Pacific/Pago_Pago",
            "Pacific/Midway|Pacific/Samoa",
            "Pacific/Midway|US/Samoa",
            "Pacific/Pohnpei|Pacific/Ponape"
        ]
    });


    return moment;
}));